import { EntityChange, LayerChange } from './layerTypes'

/**
 * What changed in what the canvas is showing: everything, or - while it is
 * focused on an entity - that entity and what is inside it, however deep.
 */
export function scopeToEntity(
  entities: readonly EntityChange[],
  layers: readonly LayerChange[],
  focusedEntityUUID: string | undefined
): { entities: EntityChange[]; layers: LayerChange[] } {
  if (!focusedEntityUUID)
    return { entities: [...entities], layers: [...layers] }

  const parentOf = new Map(
    entities.map(entity => [entity.uuid, entity.parentEntityUUID])
  )

  const isShown = (uuid: string | null) => {
    const seen = new Set<string>()

    for (let id = uuid; id && !seen.has(id); id = parentOf.get(id) ?? null) {
      if (id === focusedEntityUUID) return true
      seen.add(id)
    }

    return false
  }

  return {
    entities: entities.filter(entity => isShown(entity.uuid)),
    layers: layers.filter(layer => isShown(layer.entityUUID)),
  }
}

/**
 * The entities a comparison marks on the canvas: what changed, and the Sections
 * holding them, which are how a reader reaches what is inside.
 *
 * One set for the overlays and the dimming both, so a Section kept for
 * navigation cannot end up dimmed but overlaid.
 */
export function markedEntityUUIDs(
  entities: readonly EntityChange[]
): Set<string> {
  const marked = new Set<string>()

  for (const entity of entities) {
    marked.add(entity.uuid)
    if (entity.parentEntityUUID) marked.add(entity.parentEntityUUID)
  }

  return marked
}

/** The changed layers of each entity, by the entity's uuid. */
export function groupLayerChangesByEntity(
  changes: readonly LayerChange[]
): Map<string, LayerChange[]> {
  const byEntity = new Map<string, LayerChange[]>()

  for (const change of changes) {
    if (!change.entityUUID) continue

    const existing = byEntity.get(change.entityUUID)
    if (existing) existing.push(change)
    else byEntity.set(change.entityUUID, [change])
  }

  return byEntity
}

/**
 * The changed layers that belong to no frame or Section.
 *
 * A page can carry layers directly, and they change like any other. Without a
 * group of their own they would be found by the comparison and then shown
 * nowhere, which reads as the page being unchanged.
 */
export function unattributedLayerChanges(
  changes: readonly LayerChange[]
): LayerChange[] {
  return changes.filter(change => !change.entityUUID)
}
